<script setup>
import { i18n } from 'boot/i18n';
import {
  HrItemLabel,
  HrListItem,
  HrOverflowMenuBtn,
} from 'src/design-system';

/**
 * A client's recorded authorizations, one row each.
 *
 * NEVER SORTED. Letters are listed in the order they were recorded, because a biller checks the
 * entry against the paper rather than looking one up by payer.
 */
defineOptions({ name: 'AuthorizationList' });

defineProps({
  /** Row view models from `presentAuthorizationRow`. */
  rows: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'remove']);

/**
 * `HrOverflowMenuBtn` takes OPTIONS, not slotted children — passing menu items as children renders an
 * empty menu with nothing to say so. Edit is here as well as on the row body, so it is reachable
 * without discovering that the whole row is clickable.
 */
const menuOptions = (row) => ([
  {
    label: i18n.t('common.buttonEdit'),
    icon: 'edit',
    action: () => emit('edit', row.authorizationId),
  },
  {
    group: 'destructive',
    options: [{
      label: i18n.t('billingAuthorizations.actionRemove'),
      icon: 'delete',
      destructive: true,
      action: () => emit('remove', row.authorizationId),
    }],
  },
]);
</script>

<template>
  <!-- No <hr-list> wrapper: the PANEL owns the list, so the add button below these rows is a sibling
       inside it and lines up with them. Same split as CoverageList and ClientCoveragePanel. -->
  <div>
    <!-- The funder and the window, and nothing else. WHAT was approved is what opening the row
         answers; inline, a letter covering four services made a six-line row of it. -->
    <hr-list-item
      v-for="row in rows"
      :key="row.authorizationId"
      segmented
      :label="row.fundingSourceName"
      :caption="row.summary"
      :clickable="!readonly"
      data-e2e="row-authorization"
      @click="!readonly && emit('edit', row.authorizationId)"
    >
      <template #trailing>
        <div class="row items-center no-wrap q-gutter-sm">
          <!-- Muted text rather than a chip, matching the coverage rows beside these. -->
          <hr-item-label
            v-if="row.attentionLabel"
            caption
            class="authorization-list__status"
          >
            {{ row.attentionLabel }}
          </hr-item-label>

          <hr-overflow-menu-btn
            v-if="!readonly"
            :options="menuOptions(row)"
            data-e2e="btn-authorization-menu"
            @click.stop
          />
        </div>
      </template>
    </hr-list-item>
  </div>
</template>

<style lang="scss" scoped>
.authorization-list__status {
  white-space: nowrap;
  color: var(--app-on-surface-variant);
}
</style>
